// Detects whether this is a returning user session and pre-selects the
// panel mode accordingly:
//   - New session / page reload: show briefing (default)
//   - Returning user navigating within the same page lifecycle: restore last thread
//
// Persists last-active-thread to sessionStorage for intra-session navigation.
// A full page reload always clears the persisted state so the user starts fresh.
import { useEffect, useRef } from 'react';
import { useShellStore } from '../../../shell/store/shellStore';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { getMessageCache } from '../cache/messageCacheInstance';
import type { ThreadMeta } from '../store/esaDomainStore';

const LAST_THREAD_KEY = 'esa:lastThreadId';
const LAST_THREAD_META_KEY = 'esa:lastThreadMeta';
const SESSION_MARK_KEY = 'esa:sessionActive';

// ─── Clear cache on full page reload ─────────────────────────────────────────
// Runs once at module load time (before any hook invocation). If the browser
// reports the current navigation as a reload, all persisted ESA session data is
// wiped so the panel always starts in a clean briefing state after a refresh.
try {
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const isReload = navEntry?.type === 'reload';
  if (isReload) {
    sessionStorage.removeItem(LAST_THREAD_KEY);
    sessionStorage.removeItem(LAST_THREAD_META_KEY);
    sessionStorage.removeItem(SESSION_MARK_KEY);
  }
} catch {
  // Performance API or sessionStorage unavailable — ignore
}

// ─── Synchronous Zustand subscription ────────────────────────────────────────
// Write to sessionStorage on every store mutation that involves the active
// thread.  Zustand's `subscribe` callback runs synchronously (before any React
// re-render or useEffect), so this is reliable even when the user reloads the
// tab mid-render.
let _persistUnsubscribe: (() => void) | null = null;

function startPersisting(): void {
  if (_persistUnsubscribe) return; // already running
  _persistUnsubscribe = useEsaDomainStore.subscribe((state) => {
    const activeId = state.activeThreadId;
    if (!activeId) return;
    try {
      sessionStorage.setItem(LAST_THREAD_KEY, activeId);
      const meta = state.threadIndex.get(activeId);
      if (meta) {
        sessionStorage.setItem(LAST_THREAD_META_KEY, JSON.stringify(meta));
      }
    } catch {
      // sessionStorage unavailable (private browsing, sandboxed iframe) — ignore
    }
  });
}

// ─── Public helpers ───────────────────────────────────────────────────────────

/**
 * Call this before mounting EsaPanel with a new-conversation intent.
 * Removes the persisted last-thread from sessionStorage so that
 * `useReturningUser` does not restore the old thread on the next mount.
 */
export function clearNewConversationSession(): void {
  try {
    sessionStorage.removeItem(LAST_THREAD_KEY);
    sessionStorage.removeItem(LAST_THREAD_META_KEY);
  } catch {
    // sessionStorage unavailable — silently ignore
  }
}

export interface UseReturningUserOptions {
  /** If true the panel startup runs without prompting — useful for tests. */
  skipSessionCheck?: boolean;
}

/**
 * Call at the EsaPanel root.
 *
 * Side effects (run once on mount):
 *  1. Start the synchronous Zustand → sessionStorage persistence subscription.
 *  2. Read `sessionStorage` for last active thread + its cached metadata.
 *  3. If found: restore activeThreadId + threadMeta (incl. title) in esaDomainStore,
 *     switch mode to 'chat' unless an explicit mode (e.g. inbox) was already set.
 *  4. Write session mark so subsequent opens within the same tab are "returning".
 */
export function useReturningUser(options: UseReturningUserOptions = {}): void {
  const initialized = useRef(false);
  const setEsaPanelMode = useShellStore(s => s.setEsaPanelMode);
  const esaPanelMode = useShellStore(s => s.esaPanelMode);
  const setActiveThread = useEsaDomainStore(s => s.setActiveThread);
  const upsertThread = useEsaDomainStore(s => s.upsertThread);

  useEffect(() => {
    // Start the synchronous persistence subscription as early as possible.
    if (!options.skipSessionCheck) startPersisting();

    if (initialized.current || options.skipSessionCheck) return;
    initialized.current = true;

    try {
      const lastThreadId = sessionStorage.getItem(LAST_THREAD_KEY);
      const sessionWasActive = sessionStorage.getItem(SESSION_MARK_KEY) === '1';

      if (sessionWasActive && lastThreadId) {
        // Restore thread metadata (including title) so the header shows correctly
        // without waiting for any API call.
        const rawMeta = sessionStorage.getItem(LAST_THREAD_META_KEY);
        if (rawMeta) {
          try {
            const meta = JSON.parse(rawMeta) as ThreadMeta;
            if (meta.id === lastThreadId) {
              upsertThread(meta);
            }
          } catch {
            // Corrupted JSON — ignore and fall through
          }
        }

        setActiveThread(lastThreadId);
        getMessageCache().setPinnedThread(lastThreadId);
        // Only switch to chat when no explicit mode was set before mount
        // (e.g. collapsed rail already opened inbox).
        if (esaPanelMode === 'briefing') {
          setEsaPanelMode('chat');
        }
      }

      sessionStorage.setItem(SESSION_MARK_KEY, '1');
    } catch {
      // sessionStorage unavailable (private browsing, iframe sandbox) — silently ignore
    }
  }, [setActiveThread, setEsaPanelMode, upsertThread, esaPanelMode, options.skipSessionCheck]);
}
